import { Icon } from "@core/components/Icon";
import { LinkBehaviour } from "@core/components/LinkBehaviour";
import classnames from "classnames";

import type { DirectionalIconComponent } from "@jti/brand-icons";

import type { SafeHtmlAttributes } from "../../types/SafeHtmlAttributes";

import styles from "./IconButton.module.scss";

type IconButtonBaseProps = {
  icon: DirectionalIconComponent;
  compact?: boolean;
  inversed?: boolean;
  rounded?: boolean;
  scrim?: boolean;
};

export type IconButtonLinkProps = IconButtonBaseProps &
  SafeHtmlAttributes<HTMLAnchorElement> & {
    href: string;
    nativeBehaviour?: boolean; // force a native anchor element even when a link component is provided via context
    target?: string;
    disabled?: boolean;
    rel?: string;
  };

export type IconButtonButtonProps = IconButtonBaseProps &
  SafeHtmlAttributes<HTMLButtonElement> & {
    href?: never;
    type?: "button" | "submit" | "reset";
    disabled?: boolean;
    name?: string;
    value?: string;
    tabIndex?: number;
  };

export type IconButtonProps = IconButtonLinkProps | IconButtonButtonProps;

export const IconButton = (props: IconButtonProps) => {
  const {
    icon: IconComponent,
    compact = false,
    inversed = false,
    rounded = false,
    scrim = false,
    disabled = false,
    ...restProps
  } = props;

  const classes = classnames(styles.iconButton, {
    [styles.compact]: compact,
    [styles.inverse]: inversed,
    [styles.rounded]: rounded,
    [styles.scrim]: scrim,
    [styles.disabled]: disabled,
  });

  const content = <Icon icon={IconComponent} size="sm" />;

  if (typeof restProps.href === "string") {
    const { href, onClick, ...anchorProps } = restProps;

    return (
      <LinkBehaviour
        {...anchorProps}
        href={href}
        className={classes}
        aria-disabled={disabled || undefined}
        onClick={(event) => {
          if (disabled) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          onClick?.(event);
        }}
      >
        {content}
      </LinkBehaviour>
    );
  }

  const { type = "button", ...buttonProps } = restProps;

  return (
    <button {...buttonProps} type={type} className={classes} disabled={disabled}>
      {content}
    </button>
  );
};
